// The orrery globe as the body's own shape, not as a sphere.
//
// Vesta is 289 x 280 x 229 km and Phobos 13.0 x 11.4 x 9.1, and a SphereGeometry
// at the registry radius draws neither of them: the shape only arrived once the
// height tiles streamed in under the camera, and the takeover popped. Here the
// globe is a cubesphere displaced by the SAME height field the tiles are built
// on - the six L0 tiles of the pyramid's height role, on the pyramid datum - so
// the globe and the tile ground are one surface at every band (docs/ATLAS-TERRAIN.md,
// "One sphere").
//
// The equirect albedo map is not sampled through this mesh's uv attribute: a
// cubesphere has no antimeridian to cut along, so the longitude is recovered
// per fragment in `SHAPE_MAP_FRAGMENT` instead.

import * as THREE from 'three';

import { bilinearSample } from './plotField.ts';
import {
  parsePyramidManifest, pyramidUrl, tileUrl, dequantizeH16, parseH16Header,
  type PyramidManifest,
} from './tileSource.ts';
import { faceTangent, faceUvForDirection, type Vec3 } from './tileTree.ts';
import type { BodyRow } from '../world/types.ts';

/**
 * Quads per cubesphere face edge.
 *
 * The datum sphere itself is never the error: 4 * 128 segments around a great
 * circle is a polyhedral sagitta of R * pi^2 / (2 * 512^2), 0.005 px of the
 * 248 px radius ORBIT draws a body at over a 900-row pane. What the number buys
 * is how well the grid resolves the L0 field, and against the full 256-cell
 * field over 1.6 M directions a body the radial error at 128 quads is rms 4.5 m
 * and p99 16.2 m on Phobos, the worst body per pixel - 0.10 and 0.36 px of that
 * disc, against 0.21 and 0.79 px at 64. The cost is 99,846 vertices and 196,608
 * triangles, 5.5 MB of buffers.
 */
export const SHAPE_QUADS = 128;

/**
 * The longest a body with a shape and no ellipsoid to stand in for it (none
 * today; a row whose recorded axes are equal) is held off screen while that
 * shape loads. Past it, or once every try has failed, the body draws as its
 * sphere. The six faces take 0.5 to 0.8 s on an
 * idle line to the preview, and 6 to 15 s after the click while the Atlas's
 * own boot downloads share a 15 Mbit/s one.
 */
export const SHAPE_HOLD_MAX_MS = 15_000;

/**
 * A shape load that failed on the wire (a network change drops every request
 * in flight) is asked again, this many times in all and this far apart.
 */
export const SHAPE_LOAD_TRIES = 3;
export const SHAPE_RETRY_GAP_MS = 1000;

/**
 * The drawn size under which a shaped body may still draw as its sphere: 0.3
 * degrees is six pixels of the reference 900-row, 45 degree pane, where even
 * Psyche's 0.62 radius spread moves the outline under a pixel either way.
 */
export const SHAPE_HOLD_MIN_DEG = 0.3;

/**
 * Where a shaped body's shape is asked for without a focus: half the held
 * size, so an approach that is not a jump lands the load before the hold.
 */
export const SHAPE_ASK_MIN_DEG = SHAPE_HOLD_MIN_DEG / 2;

/**
 * The share of its radius a body's registry axes must spread by, before its
 * sphere reads as the wrong body: Enceladus's 3.3 percent is in, Mars's 0.6
 * is not, so the boot globe is not held back for relief nobody sees from orbit.
 */
const SHAPED_AXIS_SPREAD = 0.02;

/**
 * Does this row draw as a shape visibly unlike its sphere, so is never drawn
 * as that sphere once it is large on screen: a baked height role
 * that is a published shape model, or registry axes that spread past
 * `SHAPED_AXIS_SPREAD`.
 */
export function drawsAsShape(row: ShapeRow): boolean {
  if (row.availability.coverage.height !== 'baked') return false;
  if (row.bake?.global.some((g) => g.role === 'height'
    && (g.format === 'shape_grid' || g.format === 'shape_mesh'))) return true;
  return axesSpread(row) > SHAPED_AXIS_SPREAD;
}

type ShapeRow = Pick<BodyRow, 'radius_m' | 'ellipsoid' | 'bake' | 'availability'>;

function axesSpread(row: Pick<BodyRow, 'radius_m' | 'ellipsoid'>): number {
  const e = row.ellipsoid;
  return (Math.max(e.a_m, e.b_m, e.c_m) - Math.min(e.a_m, e.b_m, e.c_m)) / row.radius_m;
}

/**
 * Does a shaped row draw as its registry ellipsoid until its shape lands,
 * rather than being held: every one whose recorded axes spread, which is every
 * shaped body in the registry today.
 * The ellipsoid sits about half as far from the L0 field as the sphere does
 * (RMS 7.1 against 14.6 percent of the radius on Psyche, 3.1 against 7.0 on Pallas).
 */
export function ellipsoidStandsIn(row: ShapeRow): boolean {
  return drawsAsShape(row) && axesSpread(row) > SHAPED_AXIS_SPREAD;
}

/**
 * A row's registry ellipsoid: its `radius_m` sphere scaled per axis, in the
 * orrery's frame, where body-fixed x, y, z are three's x, -z, y (`buildShapeGeometry`).
 */
export function ellipsoidGeometry(
  row: Pick<BodyRow, 'id' | 'radius_m' | 'ellipsoid'>, widthSegments: number, heightSegments: number,
): THREE.BufferGeometry {
  const r = row.radius_m;
  const geo = new THREE.SphereGeometry(r, widthSegments, heightSegments);
  geo.scale(row.ellipsoid.a_m / r, row.ellipsoid.c_m / r, row.ellipsoid.b_m / r);
  geo.name = `ellipsoid:${row.id}`;
  return geo;
}

/** Six L0 height tiles, dequantised: the field the globe is displaced by. */
export interface ShapeField {
  readonly datumRadiusM: number;
  /** Height samples across one tile edge, apron excluded. */
  readonly cells: number;
  readonly apron: number;
  /** `cells + 2 * apron`. */
  readonly stored: number;
  /** By face, metres above the datum, row-major, `stored^2`. */
  readonly faces: readonly Float32Array[];
  /**
   * The highest sample in the whole field, metres above the datum. The bound a
   * sweep has to miss before it can rule the drawn body out without sampling it.
   */
  readonly maxHeightM: number;
  /** What the six tiles cost on the wire. */
  readonly wireBytes: number;
  /** Height tiles this field asked the network for. */
  readonly tiles: number;
}

/**
 * `[x, y, z]`, unit length, or +X when the vector has no length to normalise.
 * A ship at the body's exact centre has no bearing for the field to answer,
 * and +X always resolves to a finite sample rather than the zero vector the
 * old bare `Vector3.normalize()` left unchanged, which `faceUvForDirection`
 * (src/atlas/tileTree.ts) then threw on for having no face. `shapeFieldRadiusM`
 * below is where production reads a direction through this.
 */
export function safeUnitDir(x: number, y: number, z: number): Vec3 {
  const len = Math.hypot(x, y, z);
  return len === 0 ? [1, 0, 0] : [x / len, y / len, z / len];
}

/** Metres above the datum under a body-fixed direction, from the L0 field. */
export function shapeHeightAt(field: ShapeField, dir: Vec3): number {
  const f = faceUvForDirection(dir);
  const face = field.faces[f.face];
  if (!face) return 0;
  return bilinearSample(
    face, field.stored, field.stored,
    f.u * field.cells + field.apron - 0.5,
    f.v * field.cells + field.apron - 0.5,
  );
}

/** A body's pyramid.json, or null where it has no pyramid. */
async function fetchPyramidManifest(
  body: string, baseUrl: string, fetchImpl: typeof fetch, signal?: AbortSignal,
): Promise<PyramidManifest | null> {
  const res = await fetchImpl(pyramidUrl(baseUrl, body), signal ? { signal } : {});
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`pyramid.json for ${body}: HTTP ${res.status} ${res.statusText}`);
  return parsePyramidManifest(await res.json());
}

/**
 * The six L0 height tiles of a body's pyramid, or null where it has no height
 * role to read.
 *
 * The bytes are fetched here and dequantised by `dequantizeH16`, which is the
 * decode the tile worker runs (`field-agreement.mjs` is the reading that the
 * worker's field equals this one). `manifest` is a pyramid.json already asked
 * for, which is then not fetched again.
 */
export async function loadShapeField(
  body: string, baseUrl: string, signal: AbortSignal,
  fetchImpl: typeof fetch = fetch,
  onTileFetch: () => void = () => {},
  manifest?: Promise<PyramidManifest | null>,
): Promise<ShapeField | null> {
  const m = await (manifest ?? fetchPyramidManifest(body, baseUrl, fetchImpl, signal));
  if (!m) return null;
  const role = m.roles.get('height');
  if (!role || role.top_level !== 0) return null;
  const faces: Float32Array[] = [];
  let wireBytes = 0;
  const bufs = await Promise.all([0, 1, 2, 3, 4, 5].map(async (face) => {
    const url = tileUrl(baseUrl, { body, face, level: 0, x: 0, y: 0 }, 'height', role.stamp);
    onTileFetch();
    const r = await fetchImpl(url, { signal });
    if (!r.ok) throw new Error(`${body} L0 height face ${face}: HTTP ${r.status} ${r.statusText}`);
    return r.arrayBuffer();
  }));
  let maxHeightM = -Infinity;
  for (const buf of bufs) {
    const header = parseH16Header(buf);
    if (header.stored !== role.stored_per_edge) {
      throw new Error(`${body} L0 height: header says ${header.stored} stored samples per edge, `
        + `pyramid.json says ${role.stored_per_edge}`);
    }
    wireBytes += buf.byteLength;
    const face = dequantizeH16(buf);
    for (const h of face) if (h > maxHeightM) maxHeightM = h;
    faces.push(face);
  }
  return {
    datumRadiusM: m.datum_radius_m,
    cells: role.cells_per_edge,
    apron: role.apron_cells,
    stored: role.stored_per_edge,
    faces,
    maxHeightM: Number.isFinite(maxHeightM) ? maxHeightM : 0,
    wireBytes,
    tiles: faces.length,
  };
}

/**
 * One axis of a face's vertex grid: the field's sample coordinate at each
 * vertex, the face tangent there, and the tangent half a mesh step either side,
 * which is the stencil the normal comes from.
 *
 * `s = f * cells + apron - 0.5` is `leafHeightAt`'s own mapping for an L0 leaf,
 * whose bounds are the whole face: sample centres, with the tile's angular edge
 * half a sample outside the first interior sample.
 */
function axis(quads: number, cells: number, apron: number): {
  s: Float64Array; tan: Float64Array; tanLo: Float64Array; tanHi: Float64Array;
} {
  const n = quads + 1;
  const s = new Float64Array(n);
  const tan = new Float64Array(n);
  const tanLo = new Float64Array(n);
  const tanHi = new Float64Array(n);
  const half = 0.5 / quads;
  for (let i = 0; i < n; i++) {
    const f = i / quads;
    s[i] = f * cells + apron - 0.5;
    tan[i] = faceTangent(f);
    tanLo[i] = faceTangent(f - half);
    tanHi[i] = faceTangent(f + half);
  }
  return { s, tan, tanLo, tanHi };
}

/**
 * A mesh's own undisplaced surface along a straight ray out from centre,
 * world metres: `row.radius_m` (the geometry's own basis - see
 * `buildShapeGeometry`'s `unitRadiusM` doc below) times whatever scale is
 * currently applied. Distinct from `drawRadiusM`, the DTM's own datum, which
 * this only equals where the scale carries no angular-floor magnification.
 *
 * Takes the row itself, not a pre-picked number: `drawRadiusM` is not a
 * `radius_m` and passing it here is a type error, not a silent swap back to
 * the pre-fix fallback.
 */
export function meshDrawnRadiusM(row: { readonly radius_m: number }, scaleX: number): number {
  return row.radius_m * scaleX;
}

/**
 * A shape-model body's surface radius along a body-fixed direction, metres at
 * the body's own size: `safeUnitDir` runs first, so a ship at the body's
 * exact centre still resolves to a finite sample, then `sampleHeightM` reads
 * the field under that direction. `sampleHeightM` is the caller's own
 * mesh-rotation-and-field lookup (`shapeHeightAt` behind the mesh's world
 * quaternion in src/atlas.ts), so this stays free of THREE and the live
 * scene - `shapeSurfaceRadiusSimM` and the contact law's `shapeSurfaceRadiusM`
 * (src/atlas.ts) both resolve through this, so the guard cannot be reached by
 * one and skipped by the other.
 */
export function shapeFieldRadiusM(
  drawRadiusM: number, datumRadiusM: number,
  sampleHeightM: (dir: Vec3) => number,
  x: number, y: number, z: number,
): number {
  const h = sampleHeightM(safeUnitDir(x, y, z));
  return drawRadiusM * (1 + h / datumRadiusM);
}

/** The equal-angle permutation `directionForFaceUv` applies, with the tangents in hand. */
function faceDir(face: number, a: number, c: number, out: [number, number, number]): void {
  const sgn = face % 2 === 0 ? 1 : -1;
  if (face < 2) { out[0] = sgn; out[1] = a; out[2] = c; } else if (face < 4) {
    out[0] = a; out[1] = sgn; out[2] = c;
  } else { out[0] = a; out[1] = c; out[2] = sgn; }
}

/**
 * The cubesphere, displaced by the field.
 *
 * `unitRadiusM` is the radius the DATUM sphere is drawn at in geometry units,
 * which is `row.radius_m`: the orrery scales a body's mesh by
 * `drawRadius / row.radius_m`, so building the datum at the row's radius and the
 * relief as a fraction of the pyramid datum leaves the relief scaling with the
 * draw radius and the existing scale law untouched.
 *
 * Positions and normals are in Atlas axes - `bodyToAtlas`'s (x, z, -y) - the way
 * `createTileMesh` writes them, so the globe and the leaves share a frame.
 */
export function buildShapeGeometry(
  field: ShapeField, unitRadiusM: number, quads = SHAPE_QUADS,
): THREE.BufferGeometry {
  const verts = quads + 1;
  const perFace = verts * verts;
  const total = perFace * 6;
  const pos = new Float32Array(total * 3);
  const nrm = new Float32Array(total * 3);
  const uv = new Float32Array(total * 2);
  const index = new Uint32Array(quads * quads * 6 * 6);
  const tab = axis(quads, field.cells, field.apron);
  // Half a mesh step, in the field's own sample units: the central difference
  // the mesh's own facets carry, rather than the field's finer gradient.
  const halfStep = field.cells / quads / 2;
  const sLo = field.apron - 0.5;
  const sHi = field.cells + field.apron - 0.5;
  const d: [number, number, number] = [0, 0, 0];
  const p = new Float64Array(15);

  // A stencil point outside the face lands on a neighbouring face's ground, one
  // mesh step out, where this tile's one-sample apron does not reach; those few
  // read through the direction so the shading has no crease on a cube edge.
  const put = (k: number, face: number, a: number, c: number, su: number, sv: number): void => {
    faceDir(face, a, c, d);
    const inv = 1 / Math.hypot(d[0], d[1], d[2]);
    d[0] *= inv; d[1] *= inv; d[2] *= inv;
    const h = su < sLo || su > sHi || sv < sLo || sv > sHi
      ? shapeHeightAt(field, d)
      : bilinearSample(field.faces[face]!, field.stored, field.stored, su, sv);
    const r = unitRadiusM * (1 + h / field.datumRadiusM);
    p[k] = d[0] * r;
    p[k + 1] = d[1] * r;
    p[k + 2] = d[2] * r;
  };

  let tri = 0;
  for (let face = 0; face < 6; face++) {
    const base = face * perFace;
    for (let j = 0; j < verts; j++) {
      const sv = tab.s[j]!;
      const tv = tab.tan[j]!;
      for (let i = 0; i < verts; i++) {
        const su = tab.s[i]!;
        const tu = tab.tan[i]!;
        put(0, face, tu, tv, su, sv);
        put(3, face, tab.tanLo[i]!, tv, su - halfStep, sv);
        put(6, face, tab.tanHi[i]!, tv, su + halfStep, sv);
        put(9, face, tu, tab.tanLo[j]!, su, sv - halfStep);
        put(12, face, tu, tab.tanHi[j]!, su, sv + halfStep);
        const ux = p[6]! - p[3]!;
        const uy = p[7]! - p[4]!;
        const uz = p[8]! - p[5]!;
        const vx = p[12]! - p[9]!;
        const vy = p[13]! - p[10]!;
        const vz = p[14]! - p[11]!;
        let nx = uy * vz - uz * vy;
        let ny = uz * vx - ux * vz;
        let nz = ux * vy - uy * vx;
        if (nx * p[0]! + ny * p[1]! + nz * p[2]! < 0) { nx = -nx; ny = -ny; nz = -nz; }
        const nl = Math.hypot(nx, ny, nz) || 1;
        const o3 = (base + j * verts + i) * 3;
        // Body-fixed to Atlas axes, positions and normals alike: (x, z, -y).
        pos[o3] = p[0]!;
        pos[o3 + 1] = p[2]!;
        pos[o3 + 2] = -p[1]!;
        nrm[o3] = nx / nl;
        nrm[o3 + 1] = nz / nl;
        nrm[o3 + 2] = -ny / nl;
        // Only a fallback: SHAPE_MAP_FRAGMENT recovers the longitude per
        // fragment, since a per-vertex u wraps across the antimeridian.
        const rl = Math.hypot(p[0]!, p[1]!, p[2]!) || 1;
        const o2 = (base + j * verts + i) * 2;
        uv[o2] = 0.5 + Math.atan2(p[1]! / rl, p[0]! / rl) / (2 * Math.PI);
        uv[o2 + 1] = 0.5 + Math.asin(Math.min(1, Math.max(-1, p[2]! / rl))) / Math.PI;
      }
    }
    // The (u, v) parameterisation swaps handedness with the face's sign, so the
    // winding is read off the face's own first triangle rather than assumed.
    const ax = pos[base * 3]!;
    const ay = pos[base * 3 + 1]!;
    const az = pos[base * 3 + 2]!;
    const ux = pos[(base + 1) * 3]! - ax;
    const uy = pos[(base + 1) * 3 + 1]! - ay;
    const uz = pos[(base + 1) * 3 + 2]! - az;
    const vx = pos[(base + verts) * 3]! - ax;
    const vy = pos[(base + verts) * 3 + 1]! - ay;
    const vz = pos[(base + verts) * 3 + 2]! - az;
    const cross = (uy * vz - uz * vy) * ax + (uz * vx - ux * vz) * ay + (ux * vy - uy * vx) * az;
    const flip = cross < 0;
    for (let j = 0; j < quads; j++) {
      for (let i = 0; i < quads; i++) {
        const a = base + j * verts + i;
        const b = a + 1;
        const c = a + verts;
        const e = c + 1;
        if (flip) {
          index[tri++] = a; index[tri++] = c; index[tri++] = b;
          index[tri++] = b; index[tri++] = c; index[tri++] = e;
        } else {
          index[tri++] = a; index[tri++] = b; index[tri++] = c;
          index[tri++] = b; index[tri++] = e; index[tri++] = c;
        }
      }
    }
  }

  let maxRadius = 0;
  for (let k = 0; k < pos.length; k += 3) {
    const r = Math.hypot(pos[k]!, pos[k + 1]!, pos[k + 2]!);
    if (r > maxRadius) maxRadius = r;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.computeBoundingSphere();
  geo.userData[SHAPE_QUADS_KEY] = quads;
  geo.userData[SHAPE_MAX_RADIUS_KEY] = maxRadius;
  return geo;
}

/** Userdata keys on a `buildShapeGeometry` result: its quads per face edge, and
 *  its farthest vertex from the centre in geometry units. */
const SHAPE_QUADS_KEY = 'fxcivShapeQuads';
const SHAPE_MAX_RADIUS_KEY = 'fxcivShapeMaxRadius';

/** `shapeMeshRadiusAt`'s triangle, three body-fixed vertices, reused across calls. */
const meshTri = new Float64Array(9);

/**
 * Where a `buildShapeGeometry` mesh stands along a body-fixed direction out of
 * its centre, geometry units, or NaN on any other geometry.
 *
 * The one facet the direction falls in, found by its face and equal-angle
 * cell rather than by a ray against all 196,608: a cell's edges are great
 * circles through its corners, so the radial ray meets exactly the triangle
 * the drawn globe draws there, at the cost of one plane intersection.
 */
export function shapeMeshRadiusAt(geo: THREE.BufferGeometry, dir: Vec3): number {
  const quads = geo.userData[SHAPE_QUADS_KEY];
  const attr = geo.getAttribute('position');
  if (typeof quads !== 'number' || attr === undefined) return NaN;
  if (dir[0] === 0 && dir[1] === 0 && dir[2] === 0) return NaN;
  const pos = attr.array;
  const verts = quads + 1;
  const f = faceUvForDirection(dir);
  const iu = Math.min(quads - 1, Math.max(0, Math.floor(f.u * quads)));
  const iv = Math.min(quads - 1, Math.max(0, Math.floor(f.v * quads)));
  const base = f.face * verts * verts;
  const at = (i: number, j: number, k: number): void => {
    const o3 = (base + j * verts + i) * 3;
    // Atlas axes back to body-fixed, the inverse of what the build wrote.
    meshTri[k] = pos[o3]!;
    meshTri[k + 1] = -pos[o3 + 2]!;
    meshTri[k + 2] = pos[o3 + 1]!;
  };
  // The index's diagonal runs (i+1, j) to (i, j+1) on every face, whichever
  // winding it took: the ray is on (i, j)'s side of it or on (i+1, j+1)'s.
  at(iu, iv, 0);
  at(iu + 1, iv, 3);
  at(iu, iv + 1, 6);
  const t = meshTri;
  const mx = t[4]! * t[8]! - t[5]! * t[7]!;
  const my = t[5]! * t[6]! - t[3]! * t[8]!;
  const mz = t[3]! * t[7]! - t[4]! * t[6]!;
  const sideA = mx * t[0]! + my * t[1]! + mz * t[2]!;
  const sideDir = mx * dir[0] + my * dir[1] + mz * dir[2];
  if (sideA * sideDir < 0) at(iu + 1, iv + 1, 0);
  const e1x = t[3]! - t[0]!;
  const e1y = t[4]! - t[1]!;
  const e1z = t[5]! - t[2]!;
  const e2x = t[6]! - t[0]!;
  const e2y = t[7]! - t[1]!;
  const e2z = t[8]! - t[2]!;
  const nx = e1y * e2z - e1z * e2y;
  const ny = e1z * e2x - e1x * e2z;
  const nz = e1x * e2y - e1y * e2x;
  const nDir = nx * dir[0] + ny * dir[1] + nz * dir[2];
  if (nDir === 0) return NaN;
  return ((nx * t[0]! + ny * t[1]! + nz * t[2]!) / nDir) * Math.hypot(dir[0], dir[1], dir[2]);
}

/** The farthest any part of a `buildShapeGeometry` mesh stands from its
 *  centre, geometry units, or null on any other geometry. */
export function shapeMeshMaxRadius(geo: THREE.BufferGeometry): number | null {
  const r = geo.userData[SHAPE_MAX_RADIUS_KEY];
  return typeof r === 'number' && r > 0 ? r : null;
}

/**
 * The map sample, by direction rather than by the uv attribute.
 *
 * A cubesphere has no seam to split the equirect map along, so the longitude is
 * recovered per fragment. The derivative it hands `textureGrad` is unwrapped
 * across the antimeridian: leaving it alone puts a full-texture step in dFdx on
 * the one fragment column that straddles it, which selects the coarsest mip and
 * draws the seam as a blurred line.
 */
export const SHAPE_MAP_FRAGMENT = `
#ifdef USE_MAP
  {
    vec3 shapeDir = normalize(vShapeDir);
    vec2 shapeUv = vec2(
      0.5 + atan(-shapeDir.z, shapeDir.x) * 0.1591549430918953,
      0.5 + asin(clamp(shapeDir.y, -1.0, 1.0)) * 0.3183098861837907);
    vec2 shapeDx = dFdx(shapeUv);
    vec2 shapeDy = dFdy(shapeUv);
    if (abs(shapeDx.x) > 0.5) shapeDx.x -= sign(shapeDx.x);
    if (abs(shapeDy.x) > 0.5) shapeDy.x -= sign(shapeDy.x);
    diffuseColor *= textureGrad(map, shapeUv, shapeDx, shapeDy);
  }
#endif
`;

/** Userdata key: this material's map is sampled by direction, not by uv. */
const SHAPE_MAPPED = 'fxcivShapeMapped';

/**
 * Say that this globe's map is sampled by direction from here on.
 *
 * A flag rather than a hook, because three keeps ONE `onBeforeCompile` per
 * material and the focused globe's own depth push and aerial chunk are written
 * onto the same one: a hook chained here was lost the next time the Atlas
 * installed that one, and the body then drew its cubesphere through the uv
 * fallback with a seam down the antimeridian. `installGlobeShader` in
 * src/atlas.ts is the single writer and reads this to know what to compose.
 */
export function markShapeMapped(mat: THREE.Material): void {
  if (mat.userData[SHAPE_MAPPED] === true) return;
  mat.userData[SHAPE_MAPPED] = true;
  mat.needsUpdate = true;
}

/** Whether `markShapeMapped` has been called on this material. */
export function isShapeMapped(mat: THREE.Material): boolean {
  return mat.userData[SHAPE_MAPPED] === true;
}

/** The direction-sampled map, into a shader the caller is already building. */
export function injectShapeMapping(
  shader: { vertexShader: string; fragmentShader: string },
): void {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vShapeDir;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vShapeDir = position;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vShapeDir;')
    .replace('#include <map_fragment>', SHAPE_MAP_FRAGMENT);
}

export interface ShapeGlobesConfig {
  /** `DTM_TILE_BASE`, passed in so this module has no `import.meta.env`. */
  readonly baseUrl: string;
  /** Geometry units per datum radius for a body: its registry `radius_m`. */
  readonly unitRadiusM: (bodyId: string) => number | null;
  readonly onReady: (bodyId: string, geometry: THREE.BufferGeometry, field: ShapeField) => void;
  readonly quads?: number;
  readonly fetchImpl?: typeof fetch;
  /** How many built globes to keep. The rest go back on the next focus. */
  readonly keep?: number;
}

export interface ShapeGlobesHandle {
  /** Build this body's displaced globe, once. Idempotent and safe to spam. */
  request(bodyId: string): void;
  /**
   * The body's pyramid datum off its pyramid.json alone, null with no pyramid.
   * The fetch is shared with `request`'s, so asking both costs one.
   */
  datum(bodyId: string): Promise<number | null>;
  /**
   * Height tiles fetched over the network since this handle was made.
   *
   * The Atlas adds it to the tile source's own per-role count: these are height
   * tile requests the app made, and `imagery-floor` reads that count against
   * the browser's own request log. Counted where each face is ASKED for, which
   * is what that log holds: all six go out in the same turn, so a load still on
   * the wire, one that threw on a face and one an eviction aborted are six
   * requests each.
   */
  heightFetches(): number;
  /**
   * The same tally for one body. A reading of one body's field is stated in
   * this one, since the number above moves for whatever globe loads.
   */
  heightFetchesFor(bodyId: string): number;
  /**
   * Drop a body's field entry and abort a load still in flight for it, so the
   * next `request()` asks the network for its L0 faces again. False where the
   * body held nothing to drop.
   */
  evict(bodyId: string): boolean;
  /**
   * Is a body still drawn as its sphere to be kept off screen: its shape is
   * asked for here if nothing has asked yet, and the answer is yes while that
   * load is in flight and younger than `SHAPE_HOLD_MAX_MS`, or failed with a
   * try left.
   */
  holding(bodyId: string, nowMs: number): boolean;
  /**
   * `descent-dojo --inject shape-globe-count-after-load`: count a field once its
   * load has resolved instead of once per face as each is asked for.
   */
  setCountDebug(next: { afterLoad: boolean }): void;
  /**
   * The height field a body's globe is displaced by, or null until it is here.
   * The contact law reads it so the ground a ship stops on and the ground it is
   * drawn over are one surface (`findGroundContact` in src/sim/ground.ts).
   */
  field(bodyId: string): ShapeField | null;
  /** What has been built, for the criterion to read rather than infer. */
  state(bodyId: string): {
    status: 'none' | 'loading' | 'ready' | 'absent' | 'error';
    quads: number;
    datumRadiusM: number | null;
    wireBytes: number;
    error: string | null;
  };
  dispose(): void;
}

interface Entry {
  status: 'loading' | 'ready' | 'absent' | 'error';
  geometry: THREE.BufferGeometry | null;
  field: ShapeField | null;
  error: string | null;
  /** This load's own, so dropping the entry stops its faces mid-flight. */
  abort: AbortController;
  /** The first ask's, kept across retries so the hold lapses once. */
  startedMs: number;
  tries: number;
  failedMs: number;
}

/**
 * The displaced globes, built on focus.
 *
 * Not eagerly for every row: the six L0 height tiles are 780 KB on the wire and
 * the mesh 5.5 MB of buffers, ten rows in the registry carry a height role, and
 * a body nobody has focused is at or under the angular floor - a couple of
 * pixels, where no shape is visible. So the cost is paid per body focused, and
 * `keep` bounds how many meshes stay resident.
 */
export function createShapeGlobes(config: ShapeGlobesConfig): ShapeGlobesHandle {
  const quads = config.quads ?? SHAPE_QUADS;
  const keep = config.keep ?? 4;
  const fetchImpl = config.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const entries = new Map<string, Entry>();
  /** Most recently requested last: what `keep` evicts from the front. */
  const order: string[] = [];
  let disposed = false;

  function touch(bodyId: string): void {
    const at = order.indexOf(bodyId);
    if (at >= 0) order.splice(at, 1);
    order.push(bodyId);
    for (let i = order.length - keep - 1; i >= 0; i--) {
      const old = order[i]!;
      const e = entries.get(old);
      if (!e || e.status !== 'ready') continue;
      e.geometry?.dispose();
      entries.delete(old);
      order.splice(i, 1);
    }
  }

  let heightFetches = 0;
  let countAfterLoad = false;
  const perBody = new Map<string, number>();
  const count = (bodyId: string, n: number): void => {
    heightFetches += n;
    perBody.set(bodyId, (perBody.get(bodyId) ?? 0) + n);
  };
  /**
   * Geometries `evict` dropped, freed once the body's next field is drawn: the
   * orrery goes on drawing the one it was handed until then.
   */
  const dropped = new Map<string, THREE.BufferGeometry>();
  const manifests = new Map<string, Promise<PyramidManifest | null>>();
  function manifestFor(bodyId: string): Promise<PyramidManifest | null> {
    const have = manifests.get(bodyId);
    if (have) return have;
    const p = fetchPyramidManifest(bodyId, config.baseUrl, fetchImpl);
    manifests.set(bodyId, p);
    // A failed fetch is asked again next time rather than held.
    p.catch(() => { if (manifests.get(bodyId) === p) manifests.delete(bodyId); });
    return p;
  }

  return {
    datum: (bodyId: string) => manifestFor(bodyId).then((m) => m?.datum_radius_m ?? null),
    heightFetches: () => heightFetches,
    heightFetchesFor: (bodyId: string) => perBody.get(bodyId) ?? 0,
    evict(bodyId: string): boolean {
      const e = entries.get(bodyId);
      if (!e) return false;
      const at = order.indexOf(bodyId);
      if (at >= 0) order.splice(at, 1);
      entries.delete(bodyId);
      // The faces of a load this drops are bytes nobody will draw.
      e.abort.abort(new Error(`${bodyId}'s shape field was dropped`));
      if (e.geometry) dropped.set(bodyId, e.geometry);
      return true;
    },
    setCountDebug(next: { afterLoad: boolean }): void {
      countAfterLoad = next.afterLoad;
    },
    request(bodyId: string): void {
      if (disposed) return;
      const have = entries.get(bodyId);
      const retry = have?.status === 'error' && have.tries < SHAPE_LOAD_TRIES
        && performance.now() - have.failedMs >= SHAPE_RETRY_GAP_MS ? have : null;
      if (have && !retry) {
        touch(bodyId);
        if (have.status === 'ready' && have.geometry && have.field) {
          config.onReady(bodyId, have.geometry, have.field);
        }
        return;
      }
      const unitRadiusM = config.unitRadiusM(bodyId);
      if (unitRadiusM === null || !(unitRadiusM > 0)) return;
      const entry: Entry = { status: 'loading', geometry: null, field: null, error: null,
        abort: new AbortController(), startedMs: retry?.startedMs ?? performance.now(),
        tries: (retry?.tries ?? 0) + 1, failedMs: 0 };
      entries.set(bodyId, entry);
      touch(bodyId);
      // Read once, at the ask: a load counted one way does not turn into the
      // other halfway through.
      const afterLoad = countAfterLoad;
      void loadShapeField(bodyId, config.baseUrl, entry.abort.signal, fetchImpl, () => {
        if (!afterLoad) count(bodyId, 1);
      }, manifestFor(bodyId)).then((field) => {
        if (disposed || entries.get(bodyId) !== entry) return;
        if (!field) { entry.status = 'absent'; return; }
        if (afterLoad) count(bodyId, field.tiles);
        entry.field = field;
        entry.geometry = buildShapeGeometry(field, unitRadiusM, quads);
        entry.geometry.name = `shape-globe:${bodyId}`;
        entry.status = 'ready';
        config.onReady(bodyId, entry.geometry, field);
        // Only now is the orrery drawing the replacement.
        dropped.get(bodyId)?.dispose();
        dropped.delete(bodyId);
      }).catch((err: unknown) => {
        if (disposed || entries.get(bodyId) !== entry) return;
        entry.status = 'error';
        entry.abort.abort(new Error(`${bodyId}'s shape load failed`));
        entry.failedMs = performance.now();
        entry.error = err instanceof Error ? err.message : String(err);
        console.warn(`[atlas] ${bodyId}'s shape globe did not load; `
          + `the globe stays its sphere or ellipsoid (try ${entry.tries} of ${SHAPE_LOAD_TRIES}):`, err);
      });
    },
    holding(bodyId: string, nowMs: number): boolean {
      const was = entries.get(bodyId);
      if (!was || was.status === 'error') this.request(bodyId);
      const e = entries.get(bodyId);
      if (!e || nowMs - e.startedMs >= SHAPE_HOLD_MAX_MS) return false;
      return e.status === 'loading' || (e.status === 'error' && e.tries < SHAPE_LOAD_TRIES);
    },
    field(bodyId: string): ShapeField | null {
      const e = entries.get(bodyId);
      return e?.status === 'ready' ? e.field : null;
    },
    state(bodyId: string) {
      const e = entries.get(bodyId);
      return {
        status: e?.status ?? 'none',
        quads,
        datumRadiusM: e?.field?.datumRadiusM ?? null,
        wireBytes: e?.field?.wireBytes ?? 0,
        error: e?.error ?? null,
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const e of entries.values()) {
        e.abort.abort(new Error('shape globes disposed'));
        e.geometry?.dispose();
      }
      for (const g of dropped.values()) g.dispose();
      dropped.clear();
      entries.clear();
      order.length = 0;
    },
  };
}
